// src/pages/MioCalendarioPage.js
import React, { useState, useEffect } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, isSameMonth, isSameDay } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Plus, X, Trash2, Filter } from "lucide-react";

const VISIBILITA_LABEL = {
  commessa:  { label: "Commessa", col: "#7c3aed", bg: "#ede9fe" },
  personale: { label: "Personale", col: "#6b7280", bg: "#f3f4f6" },
  coinvolti: { label: "Specifici", col: "#2563eb", bg: "#dbeafe" },
};

function NuovoEventoPersonale({ day, orgId, currentUser, commesse, onClose }) {
  const [form, setForm] = useState({
    titolo: "", descrizione: "", oraInizio: "09:00", oraFine: "",
    projectId: "", visibilita: "personale",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inp = { width: "100%", padding: "6px 9px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none", boxSizing: "border-box" };

  const salva = async () => {
    if (!form.titolo.trim()) return;
    setSaving(true);
    const commessa = commesse.find(c => c.id === form.projectId);
    await addDoc(collection(db, "eventiPersonali"), {
      titolo: form.titolo.trim(),
      descrizione: form.descrizione || "",
      dueDate: format(day, "yyyy-MM-dd"),
      oraInizio: form.oraInizio || "",
      oraFine: form.oraFine || "",
      visibilita: form.visibilita,
      projectId: form.projectId || null,
      projectName: commessa?.name || "",
      clienteNome: commessa?.clienteNome || "",
      autoreUid: currentUser?.uid,
      autoreNome: currentUser?.displayName || "Utente",
      orgId, createdAt: serverTimestamp(),
    });
    setSaving(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>Nuovo evento personale</h3>
          <button className="btn-icon" onClick={onClose}><X size={14}/></button>
        </div>
        <div style={{ fontSize: 12, color: "#7c3aed", fontWeight: 600, marginBottom: 12 }}>
          📅 {format(day, "EEEE d MMMM yyyy", { locale: it })}
        </div>
        <div className="form-group">
          <label className="form-label">Titolo *</label>
          <input style={inp} value={form.titolo} onChange={e => upd("titolo", e.target.value)} autoFocus placeholder="Es. Preparare relazione tecnica"/>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
          <div><label className="form-label">Ora inizio</label><input type="time" style={inp} value={form.oraInizio} onChange={e => upd("oraInizio", e.target.value)}/></div>
          <div><label className="form-label">Ora fine</label><input type="time" style={inp} value={form.oraFine} onChange={e => upd("oraFine", e.target.value)}/></div>
        </div>
        <div className="form-group">
          <label className="form-label">Descrizione</label>
          <textarea style={{ ...inp, minHeight: 50, resize: "vertical" }} value={form.descrizione} onChange={e => upd("descrizione", e.target.value)}/>
        </div>
        <div className="form-group">
          <label className="form-label">Collega a commessa (opzionale)</label>
          <select style={inp} value={form.projectId} onChange={e => upd("projectId", e.target.value)}>
            <option value="">— Nessuna commessa —</option>
            {commesse.map(c => <option key={c.id} value={c.id}>{c.codiceCommessa ? `[${c.codiceCommessa}] ` : ""}{c.name}</option>)}
          </select>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>Il collegamento non rende l'evento pubblico</div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button className="btn btn-primary" onClick={salva} disabled={!form.titolo.trim() || saving}>
            {saving ? "..." : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MioCalendarioPage({ onNavigate }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const [current, setCurrent] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  // Dati
  const [eventiCommessa, setEventiCommessa] = useState([]);
  const [eventiPersonali, setEventiPersonali] = useState([]);
  const [commesseResponsabile, setCommesseResponsabile] = useState([]);
  const [commesseInvolto, setCommesseInvolto] = useState([]);
  const [tutteCommesse, setTutteCommesse] = useState([]);
  // Filtri
  const [mostraPersonali, setMostraPersonali] = useState(true);
  const [mostraCommesse, setMostraCommesse] = useState(true);
  const [mostraCoinvolti, setMostraCoinvolti] = useState(true);
  const [commesseFiltro, setCommesseFiltro] = useState({}); // {id: true/false}

  // Carica commesse di cui sono responsabile
  useEffect(() => {
    if (!effectiveOrgId || !currentUser?.uid) return;
    return onSnapshot(
      query(collection(db, "projects"),
        where("organizationId", "==", effectiveOrgId),
        where("responsabileUid", "==", currentUser.uid),
        where("archived", "==", false)),
      snap => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !p.isParent);
        setCommesseResponsabile(list);
        // Init filtri
        const filtri = {};
        list.forEach(c => { filtri[c.id] = true; });
        setCommesseFiltro(f => ({ ...filtri, ...f }));
      }
    );
  }, [effectiveOrgId, currentUser?.uid]);

  // Carica tutte le commesse per il select nuovo evento
  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(
      query(collection(db, "projects"), where("organizationId", "==", effectiveOrgId), where("archived", "==", false)),
      snap => setTutteCommesse(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !p.isParent))
    );
  }, [effectiveOrgId]);

  // Carica eventi commessa (dove sono responsabile o coinvolto)
  useEffect(() => {
    if (!effectiveOrgId || !currentUser?.uid) return;
    return onSnapshot(
      query(collection(db, "eventiCommessa"), where("organizationId", "==", effectiveOrgId)),
      snap => {
        const tutti = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Filtra: sono responsabile O sono nei coinvoltiUid
        const miei = tutti.filter(ev =>
          ev.responsabileUid === currentUser.uid ||
          (ev.coinvoltiUid || []).includes(currentUser.uid)
        );
        setEventiCommessa(miei);
      }
    );
  }, [effectiveOrgId, currentUser?.uid]);

  // Carica eventi personali
  useEffect(() => {
    if (!effectiveOrgId || !currentUser?.uid) return;
    return onSnapshot(
      query(collection(db, "eventiPersonali"),
        where("orgId", "==", effectiveOrgId),
        where("autoreUid", "==", currentUser.uid)),
      snap => setEventiPersonali(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    );
  }, [effectiveOrgId, currentUser?.uid]);

  // Aggrega eventi per giorno
  const getEventiGiorno = date => {
    const s = format(date, "yyyy-MM-dd");
    const risultati = [];
    if (mostraPersonali) {
      eventiPersonali.filter(e => e.dueDate === s).forEach(e => risultati.push({ ...e, _tipo: "personale" }));
    }
    if (mostraCommesse) {
      eventiCommessa.filter(e => e.dueDate === s && e.responsabileUid === currentUser?.uid)
        .filter(e => commesseFiltro[e.projectId] !== false)
        .forEach(e => risultati.push({ ...e, _tipo: "commessa-resp" }));
    }
    if (mostraCoinvolti) {
      eventiCommessa.filter(e => e.dueDate === s && (e.coinvoltiUid||[]).includes(currentUser?.uid) && e.responsabileUid !== currentUser?.uid)
        .forEach(e => risultati.push({ ...e, _tipo: "coinvolto" }));
    }
    return risultati.sort((a, b) => (a.oraInizio||"").localeCompare(b.oraInizio||""));
  };

  const eliminaEventoPersonale = async id => {
    await deleteDoc(doc(db, "eventiPersonali", id));
  };

  const coloreEvento = tipo => {
    if (tipo === "personale") return { bg: "#f3f4f6", col: "#374151" };
    if (tipo === "commessa-resp") return { bg: "#ede9fe", col: "#5b21b6" };
    return { bg: "#dbeafe", col: "#1d4ed8" };
  };

  const renderGrid = () => {
    const start = startOfWeek(startOfMonth(current), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(current), { weekStartsOn: 1 });
    const today = new Date();
    const cells = [];
    let day = start;
    while (day <= end) {
      const d = new Date(day);
      const evG = getEventiGiorno(d);
      const isToday = isSameDay(d, today);
      const isCurrentMonth = isSameMonth(d, current);
      const isSelected = selectedDay && isSameDay(d, selectedDay);
      const isSabDom = d.getDay() === 0 || d.getDay() === 6;
      cells.push(
        <div key={d.toString()}
          style={{ border: `2px solid ${isSelected ? "#7c3aed" : isToday ? "#c4b5fd" : "#f3f4f6"}`,
            borderRadius: 8, padding: "4px 6px", minHeight: 80,
            background: isSelected ? "#faf5ff" : isSabDom ? "#fafafa" : "#fff", cursor: "pointer" }}
          onClick={() => { setSelectedDay(d); setShowAdd(false); }}>
          <div style={{ fontSize: 11, fontWeight: isToday ? 800 : 500,
            color: isToday ? "#7c3aed" : !isCurrentMonth ? "#d1d5db" : isSabDom ? "#9ca3af" : "#374151", marginBottom: 4 }}>
            {isToday
              ? <span style={{ background: "#7c3aed", color: "#fff", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{format(d, "d")}</span>
              : format(d, "d")}
          </div>
          {evG.slice(0, 3).map((ev, i) => {
            const c = coloreEvento(ev._tipo);
            return (
              <div key={ev.id || i} style={{ fontSize: 10, fontWeight: 500, padding: "2px 5px", borderRadius: 4, marginBottom: 2,
                background: c.bg, color: c.col, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {ev.oraInizio && <span style={{ opacity: 0.7 }}>{ev.oraInizio} </span>}
                {ev.titolo}
              </div>
            );
          })}
          {evG.length > 3 && <div style={{ fontSize: 9, color: "#9ca3af" }}>+{evG.length - 3} altri</div>}
        </div>
      );
      day = addDays(day, 1);
    }
    return cells;
  };

  const selectedEventi = selectedDay ? getEventiGiorno(selectedDay) : [];

  return (
    <div style={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Sidebar filtri */}
      <div style={{ width: 220, borderRight: "1px solid #e5e7eb", background: "#fff", overflowY: "auto", padding: "16px 14px", flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1e1b4b", marginBottom: 14 }}>📅 Il mio calendario</div>

        {/* Filtri tipo */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Mostra</div>
          {[
            { key: "mostraPersonali", label: "📌 Personali", val: mostraPersonali, set: setMostraPersonali, col: "#6b7280" },
            { key: "mostraCommesse", label: "👷 Mie commesse", val: mostraCommesse, set: setMostraCommesse, col: "#7c3aed" },
            { key: "mostraCoinvolti", label: "👥 Sono coinvolto", val: mostraCoinvolti, set: setMostraCoinvolti, col: "#2563eb" },
          ].map(f => (
            <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, cursor: "pointer" }}>
              <input type="checkbox" checked={f.val} onChange={e => f.set(e.target.checked)}/>
              <span style={{ fontSize: 12, color: f.col, fontWeight: 600 }}>{f.label}</span>
            </label>
          ))}
        </div>

        {/* Filtri per commessa */}
        {commesseResponsabile.length > 0 && (
          <div>
            <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, textTransform: "uppercase", marginBottom: 8 }}>Commesse</div>
            {commesseResponsabile.map(c => (
              <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, cursor: "pointer" }}>
                <input type="checkbox"
                  checked={commesseFiltro[c.id] !== false}
                  onChange={e => setCommesseFiltro(f => ({ ...f, [c.id]: e.target.checked }))}/>
                <span style={{ fontSize: 11, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.codiceCommessa && <span style={{ color: "#7c3aed", fontFamily: "monospace", marginRight: 3 }}>{c.codiceCommessa}</span>}
                  {c.name}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Calendario */}
      <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <button onClick={() => setCurrent(subMonths(current, 1))}
            style={{ padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            <ChevronLeft size={16}/>
          </button>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: "#1e1b4b", textTransform: "capitalize" }}>
            {format(current, "MMMM yyyy", { locale: it })}
          </h3>
          <button onClick={() => setCurrent(addMonths(current, 1))}
            style={{ padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            <ChevronRight size={16}/>
          </button>
        </div>

        {/* Legenda */}
        <div style={{ display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: "#ede9fe" }}/><span style={{ fontSize: 11, color: "#5b21b6" }}>Mie commesse</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: "#dbeafe" }}/><span style={{ fontSize: 11, color: "#1d4ed8" }}>Sono coinvolto</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: "#f3f4f6" }}/><span style={{ fontSize: 11, color: "#374151" }}>Personali</span>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4, marginBottom: 4 }}>
          {["Lun","Mar","Mer","Gio","Ven","Sab","Dom"].map(d => (
            <div key={d} style={{ textAlign: "center", fontSize: 10, fontWeight: 700, color: "#9ca3af", padding: "4px 0", textTransform: "uppercase" }}>{d}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 4 }}>
          {renderGrid()}
        </div>
      </div>

      {/* Panel laterale giorno */}
      {selectedDay && (
        <div style={{ width: 300, borderLeft: "1px solid #e5e7eb", background: "#fff", display: "flex", flexDirection: "column", flexShrink: 0 }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e5e7eb", background: "#1e1b4b" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#fff", textTransform: "capitalize" }}>
              {format(selectedDay, "EEEE d MMMM", { locale: it })}
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.5)", marginTop: 2 }}>{selectedEventi.length} evento/i</div>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: 12 }}>
            {selectedEventi.length === 0 && (
              <div style={{ textAlign: "center", padding: "24px 0", color: "#9ca3af", fontSize: 12 }}>Nessun evento</div>
            )}
            {selectedEventi.map(ev => {
              const c = coloreEvento(ev._tipo);
              return (
                <div key={ev.id} style={{ background: c.bg, borderRadius: 8, padding: "8px 10px", marginBottom: 8, border: `1px solid ${c.col}22` }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: c.col }}>
                        {ev.oraInizio && <span style={{ marginRight: 5 }}>{ev.oraInizio}{ev.oraFine && `→${ev.oraFine}`}</span>}
                        {ev.titolo}
                      </div>
                      {ev.descrizione && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>{ev.descrizione}</div>}
                      {ev.projectName && (
                        <div style={{ fontSize: 10, color: "#7c3aed", marginTop: 3, fontWeight: 600 }}>
                          📁 {ev.projectName}
                        </div>
                      )}
                      {ev.clienteNome && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 2 }}>👤 {ev.clienteNome}</div>}
                      <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 3 }}>✏️ {ev.autoreNome}</div>
                    </div>
                    {ev._tipo === "personale" && (
                      <button onClick={() => eliminaEventoPersonale(ev.id)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5" }}>
                        <Trash2 size={12}/>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ padding: "10px 12px", borderTop: "1px solid #e5e7eb" }}>
            <button onClick={() => setShowAdd(true)}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                padding: "8px", border: "1px dashed #7c3aed", borderRadius: 8, background: "#faf5ff",
                color: "#7c3aed", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              <Plus size={13}/> Nuovo evento personale
            </button>
          </div>
        </div>
      )}

      {/* Modal nuovo evento personale */}
      {showAdd && selectedDay && (
        <NuovoEventoPersonale
          day={selectedDay}
          orgId={effectiveOrgId}
          currentUser={currentUser}
          commesse={tutteCommesse}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}
